import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Button, Result, Spin, message, Modal } from 'antd';
import { 
  CheckCircleOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  MailOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { PageContainer } from './styled';
import BookingService from '../../services/BookingService';
import moment from 'moment';

const { Text, Paragraph } = Typography;

// Helper function to format duration (accepts hours from backend)
const formatDuration = (hours) => {
  if (!hours) return '';
  const wholeHours = Math.floor(hours);
  const remainingMinutes = Math.round((hours - wholeHours) * 60);
  if (wholeHours >= 1 && remainingMinutes === 0) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
  }
  if (wholeHours >= 1) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  }
  return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
};

const BookingConfirmation = ({ bookingData, onNewBooking, onBack, onReschedule, onBookingConfirmed }) => {
  const [loading, setLoading] = useState(true);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [bookingCancelled, setBookingCancelled] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (bookingData) {
      simulateBookingCreation();
    }
  }, [bookingData]);

  const simulateBookingCreation = async () => {
    try {
      setLoading(true);
      setBookingConfirmed(false); // Reset state
      
      // Create the booking using the real API
      const bookingPayload = {
        eventTypeId: bookingData.eventType._id,
        timeSlot: bookingData.timeSlot,
        userInfo: bookingData.userInfo
      };
      
      console.log('Creating booking with payload:', bookingPayload);
      
      const response = await BookingService.createBooking(bookingPayload);
      
      console.log('Raw booking response:', response);
      console.log('Response type:', typeof response);
      console.log('Response success property:', response?.success);
      console.log('Response success type:', typeof response?.success);
      console.log('Response data property:', response?.data);
      
      // Handle undefined response
      if (!response) {
        throw new Error('No response received from booking service');
      }
      
      if (response && response.success === true && response.data && response.data.booking) {
        const confirmationCode = response.data.booking.confirmationCode;
        if (confirmationCode) {
          setConfirmationCode(confirmationCode);
          setBookingConfirmed(true);
          message.success('Booking confirmed successfully!');
          console.log('Success state set - bookingConfirmed should be true');
          
          // Update URL with confirmation code so user can bookmark/return
          if (onBookingConfirmed) {
            onBookingConfirmed(confirmationCode);
          }
        } else {
          throw new Error('No confirmation code received');
        }
      } else {
        // Handle specific error types
        const errorMessage = response?.error || 'Unknown error occurred';
        
        if (errorMessage.includes('Time slot is no longer available')) {
          // Clear any existing messages first
          message.destroy();
          message.error('The selected time slot is no longer available. Please choose a different time.');
          // Allow user to go back and select a different time
          if (onBack) {
            setTimeout(() => {
              message.destroy(); // Clear message before redirecting
              onBack();
            }, 2500); // Auto-redirect after showing message
            return;
          }
        } else {
          message.destroy(); // Clear existing messages
          message.error(errorMessage);
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      console.log('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Don't show generic error if we've already handled specific cases
      if (!error.message.includes('Time slot is no longer available')) {
        message.error('Failed to create booking. Please try again.');
      }
      
      setBookingConfirmed(false);
    } finally {
      console.log('Finally block - setting loading to false');
      setLoading(false);
    }
  };

  const generateCalendarEvent = () => {
    const { eventType, timeSlot } = bookingData;
    const startDate = moment(timeSlot.startTime).format('YYYYMMDDTHHmmss');
    const endDate = moment(timeSlot.endTime).format('YYYYMMDDTHHmmss');
    
    const calendarUrl = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Black Heart Association//Booking System//EN
BEGIN:VEVENT
UID:${confirmationCode}@blackheartassociation.org
DTSTAMP:${moment().format('YYYYMMDDTHHmmss')}Z
DTSTART:${startDate}Z
DTEND:${endDate}Z
SUMMARY:${eventType.name} - Black Heart Association
DESCRIPTION:Health screening appointment with Black Heart Association
LOCATION:Mobile Unit Location (details will be provided)
END:VEVENT
END:VCALENDAR`;

    const link = document.createElement('a');
    link.href = calendarUrl;
    link.download = `BHA-${eventType.name.replace(/\s+/g, '')}-${moment(timeSlot.date).format('YYYY-MM-DD')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCancelBooking = async () => {
    setCancelLoading(true);
    try {
      const response = await BookingService.cancelBooking(confirmationCode);
      if (response.success) {
        message.success('Booking cancelled successfully');
        setBookingCancelled(true);
        setShowCancelModal(false);
      } else {
        message.error(response.error || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      message.error('Failed to cancel booking. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReschedule = () => {
    // Navigate to reschedule flow with current booking data
    if (onReschedule) {
      onReschedule(confirmationCode, bookingData);
    } else {
      // Fallback: show message with instructions
      Modal.info({
        title: 'Reschedule Your Appointment',
        content: (
          <div>
            <p>To reschedule your appointment:</p>
            <ol style={{ paddingLeft: '20px', marginTop: '12px' }}>
              <li>Contact us at <strong>Tara@blackheartassociation.org</strong></li>
              <li>Provide your confirmation code: <Text code>{confirmationCode}</Text></li>
              <li>Let us know your preferred new date and time</li>
            </ol>
            <p style={{ marginTop: '16px', color: '#666' }}>
              We&apos;ll get back to you within 24 hours to confirm your new appointment.
            </p>
          </div>
        ),
        okText: 'Got it',
        width: 480
      });
    }
  };

  // Show cancelled state
  if (bookingCancelled) {
    return (
      <PageContainer>
        <div style={{ padding: '40px 0' }}>
          <Result
            status="success"
            title="Booking Cancelled"
            subTitle={
              <div>
                Your appointment has been cancelled successfully.
                <br />
                Confirmation code <Text code>{confirmationCode}</Text> is no longer active.
              </div>
            }
            extra={[
              <Button type="primary" key="new" onClick={onNewBooking}>
                Book New Appointment
              </Button>
            ]}
          />
        </div>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '24px', fontSize: '16px', color: '#666' }}>
            Processing your booking...
          </div>
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#999' }}>
            Please wait while we confirm your appointment
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!bookingConfirmed) {
    return (
      <PageContainer>
        <div style={{ padding: '40px 0' }}>
          <Result
            status="error"
            title="Booking Failed"
            subTitle="We were unable to process your booking. Please try again or contact us directly."
            extra={[
              <Button type="primary" key="retry" onClick={simulateBookingCreation}>
                Try Again
              </Button>,
              onBack && (
                <Button key="edit" onClick={onBack}>
                  Go Back & Edit Details
                </Button>
              ),
              <Button key="back" onClick={onNewBooking}>
                Start Over
              </Button>
            ].filter(Boolean)}
          />
        </div>
      </PageContainer>
    );
  }

  const { eventType, timeSlot, userInfo } = bookingData;

  return (
    <PageContainer>
      <div style={{ padding: '40px 0' }} className="confirmation-wrapper">
        <style>
          {`
            @media (max-width: 768px) {
              .confirmation-wrapper {
                padding: 24px 0 !important;
              }
              .confirmation-wrapper .ant-result-title {
                font-size: 24px !important;
              }
              .confirmation-wrapper .ant-result-subtitle {
                font-size: 14px !important;
              }
              .confirmation-wrapper .ant-card {
                border-radius: 8px !important;
              }
              .confirmation-wrapper .ant-card-head {
                padding: 12px 16px !important;
              }
              .confirmation-wrapper .ant-card-body {
                padding: 16px !important;
              }
            }
            @media (max-width: 480px) {
              .confirmation-wrapper {
                padding: 16px 0 !important;
              }
              .confirmation-wrapper .ant-result-title {
                font-size: 20px !important;
              }
              .confirmation-wrapper .ant-result-icon .anticon {
                font-size: 48px !important;
              }
              .confirmation-wrapper .ant-btn {
                margin-bottom: 8px !important;
                width: 100% !important;
              }
            }
          `}
        </style>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} lg={16}>
            <Result
              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              title="Booking Confirmed!"
              subTitle={
                <div style={{ fontSize: '16px', marginBottom: '24px' }}>
                  Your appointment has been successfully scheduled. 
                  <br />
                  Confirmation code: <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                    {confirmationCode}
                  </Text>
                </div>
              }
              extra={[
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={generateCalendarEvent}
                  key="calendar"
                  style={{ marginRight: '12px' }}
                >
                  Add to Calendar
                </Button>,
                <Button 
                  size="large" 
                  onClick={onNewBooking}
                  key="new"
                >
                  Book Another Appointment
                </Button>
              ]}
            />
          </Col>
        </Row>

        <Row gutter={[24, 24]} justify="center" style={{ marginTop: '32px' }}>
          <Col xs={24} lg={16}>
            <Card title="Appointment Details" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Service
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>{eventType.name}</div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Date
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {moment(timeSlot.date).format('dddd, MMMM D, YYYY')}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Time
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {moment(timeSlot.startTime).format('h:mm A')} - {moment(timeSlot.endTime).format('h:mm A')} CST
                    </div>
                  </div>
                </Col>

                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Name
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {userInfo.firstName} {userInfo.lastName}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Email
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>{userInfo.email}</div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Duration
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>{formatDuration(eventType.duration)}</div>
                  </div>
                </Col>
              </Row>
            </Card>

            <Card title="What's Next?">
              <div style={{ lineHeight: '1.8' }}>
                <Paragraph style={{ marginBottom: '16px' }}>
                  <Text strong>Confirmation Email:</Text> A confirmation email with all the details 
                  has been sent to {userInfo.email}. Please check your inbox and spam folder.
                </Paragraph>

                <Paragraph style={{ marginBottom: '16px' }}>
                  <Text strong>Before Your Appointment:</Text> You&apos;ll receive a reminder email 
                  24 hours before your scheduled appointment with the exact location and any 
                  preparation instructions.
                </Paragraph>

                <Paragraph style={{ marginBottom: '16px' }}>
                  <Text strong>Need to Reschedule?</Text> Use the options below to reschedule or 
                  cancel your appointment. Please notify us at least 24 hours in advance when possible.
                </Paragraph>

                <Paragraph style={{ margin: 0 }}>
                  <Text strong>Questions?</Text> Contact the Black Heart Association at 
                  <Text code style={{ margin: '0 4px' }}>Tara@blackheartassociation.org</Text> 
                  or call us during business hours.
                </Paragraph>
              </div>
            </Card>

            {/* Cancel/Reschedule Actions Card */}
            <Card 
              title="Need to Change Your Appointment?" 
              style={{ marginTop: '24px' }}
              className="actions-card"
            >
              <style>
                {`
                  @media (max-width: 480px) {
                    .actions-card .ant-row {
                      gap: 12px !important;
                    }
                    .actions-card .ant-btn {
                      width: 100% !important;
                    }
                  }
                `}
              </style>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    padding: '20px', 
                    background: '#f0f5ff', 
                    borderRadius: '8px',
                    border: '1px solid #adc6ff',
                    height: '100%'
                  }}>
                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#1890ff' }}>
                      <SwapOutlined style={{ marginRight: '8px' }} />
                      Reschedule Appointment
                    </Text>
                    <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                      Need a different time? Let us know and we&apos;ll help you find a new slot.
                    </Paragraph>
                    <Button 
                      type="primary" 
                      ghost
                      onClick={handleReschedule}
                      icon={<SwapOutlined />}
                    >
                      Reschedule
                    </Button>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    padding: '20px', 
                    background: '#fff1f0', 
                    borderRadius: '8px',
                    border: '1px solid #ffa39e',
                    height: '100%'
                  }}>
                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#ff4d4f' }}>
                      <StopOutlined style={{ marginRight: '8px' }} />
                      Cancel Appointment
                    </Text>
                    <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                      Can&apos;t make it? Cancel your appointment to free up the slot for others.
                    </Paragraph>
                    <Button 
                      danger
                      onClick={() => setShowCancelModal(true)}
                      icon={<StopOutlined />}
                    >
                      Cancel Booking
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        title={
          <span style={{ color: '#ff4d4f' }}>
            <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
            Cancel Booking
          </span>
        }
        open={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowCancelModal(false)}>
            Keep My Booking
          </Button>,
          <Button 
            key="cancel" 
            danger 
            loading={cancelLoading}
            onClick={handleCancelBooking}
          >
            Yes, Cancel Booking
          </Button>
        ]}
      >
        <div style={{ padding: '16px 0' }}>
          <Paragraph>
            Are you sure you want to cancel this appointment?
          </Paragraph>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '16px' 
          }}>
            <Text strong>Confirmation Code:</Text> <Text code>{confirmationCode}</Text>
            <br />
            <Text strong>Date:</Text> {moment(timeSlot.date).format('dddd, MMMM D, YYYY')}
            <br />
            <Text strong>Time:</Text> {moment(timeSlot.startTime).format('h:mm A')}
          </div>
          <Paragraph type="warning" style={{ marginBottom: 0 }}>
            This action cannot be undone. You&apos;ll need to book a new appointment if you change your mind.
          </Paragraph>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default BookingConfirmation;
